import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import Nav from '../../IDE/components/Header/Nav';
import { RootPage } from '../../../components/RootPage';
import { Loader } from '../../App/components/Loader';
import DocsSidebar, { docsHref } from '../components/DocsSidebar';
import DocsMarkdown from '../components/DocsMarkdown';
import {
  DocsArticle,
  DocsFooterNav,
  DocsLayout,
  DocsMain,
  DocsMessage
} from '../Docs.styles';
import {
  DocsManifest,
  docsNeighbors,
  fetchDocsManifest,
  fetchDocsPage,
  findDocsPage,
  resolveDocsLang
} from '../docsContent';

type Status = 'loading' | 'ready' | 'notFound' | 'error';

/**
 * `/ar-reference` and `/ar-reference/:slug` — the AstroTour[AR] reference
 * (Help ▸ AstroTour[AR] reference). Public: no auth, like `/examples` and `/about`.
 *
 * With no slug it shows the first page of the first section, without redirecting — so the
 * canonical entry URL stays clean and the back button doesn't bounce.
 */
const DocsView = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug?: string }>();
  const lang = resolveDocsLang(i18n.language);

  const [manifest, setManifest] = useState<DocsManifest | null>(null);
  const [markdown, setMarkdown] = useState('');
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let cancelled = false;
    fetchDocsManifest(lang)
      .then((loaded) => {
        if (!cancelled) setManifest(loaded);
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const page = findDocsPage(manifest, slug);

  useEffect(() => {
    let cancelled = false;
    if (manifest && !page) {
      setStatus('notFound');
    } else if (manifest && page) {
      setStatus('loading');
      fetchDocsPage(lang, page.file)
        .then((text) => {
          if (cancelled) return;
          setMarkdown(text);
          setStatus('ready');
        })
        .catch(() => {
          if (!cancelled) setStatus('error');
        });
    }
    return () => {
      cancelled = true;
    };
    // `page` is derived from manifest + slug, so its file path is the real dependency.
  }, [lang, manifest, page?.file]); // eslint-disable-line

  const sectionTitle = t('Docs.Title', {
    defaultValue: 'AstroTour[AR] reference'
  });
  const { prev, next } = docsNeighbors(manifest, page?.slug);

  return (
    <RootPage>
      <Helmet>
        <title>{page ? `${page.title} | ${sectionTitle}` : sectionTitle}</title>
      </Helmet>
      <Nav layout="dashboard" />

      {!manifest && status === 'loading' && <Loader />}

      {status === 'error' && (
        <DocsLayout>
          <DocsArticle>
            <h1>{sectionTitle}</h1>
            <DocsMessage>
              {t('Docs.LoadError', {
                defaultValue:
                  'The documentation could not be loaded. Please try again.'
              })}
            </DocsMessage>
          </DocsArticle>
        </DocsLayout>
      )}

      {manifest && status !== 'error' && (
        <DocsLayout>
          <DocsSidebar
            manifest={manifest}
            currentSlug={page?.slug}
            label={sectionTitle}
          />
          {status === 'notFound' ? (
            <DocsArticle>
              <h1>
                {t('Docs.NotFoundTitle', { defaultValue: 'Page not found' })}
              </h1>
              <DocsMessage>
                <Link to="/ar-reference">
                  {t('Docs.BackToStart', {
                    defaultValue: 'Back to the start of the AR reference'
                  })}
                </Link>
              </DocsMessage>
            </DocsArticle>
          ) : (
            <DocsMain>
              {status === 'loading' ? (
                <DocsArticle>
                  <DocsMessage>
                    {t('Common.Loading', { defaultValue: 'Loading…' })}
                  </DocsMessage>
                </DocsArticle>
              ) : (
                <DocsMarkdown markdown={markdown} pageTitle={page?.title} />
              )}
              <DocsFooterNav
                aria-label={t('Docs.PageNav', {
                  defaultValue: 'Previous and next page'
                })}
              >
                <span>
                  {prev && <Link to={docsHref(prev.slug)}>← {prev.title}</Link>}
                </span>
                <span>
                  {next && <Link to={docsHref(next.slug)}>{next.title} →</Link>}
                </span>
              </DocsFooterNav>
            </DocsMain>
          )}
        </DocsLayout>
      )}
    </RootPage>
  );
};

export default DocsView;
